import type {
  ActiveGuideQuest,
  ClaimGuideQuestResult,
  GuideQuestDefinition,
  GuideQuestEvent,
  GuideQuestProgressState,
  GuideQuestSaveState,
} from "./types.js";

function normalizeProgress(
  questId: string,
  targetCount: number,
  state?: GuideQuestProgressState,
): GuideQuestProgressState {
  const progress = Math.max(0, Math.floor(state?.progress ?? 0));
  const capped = Math.min(progress, targetCount);
  return {
    questId,
    progress: capped,
    completed: Boolean(state?.completed) || capped >= targetCount,
    claimed: Boolean(state?.claimed),
  };
}

function matchesTarget(expected: string, actual: string, kind: GuideQuestEvent["kind"]): boolean {
  if (kind === "chapterReached" && expected !== "any") {
    const expectedChapter = Number(expected);
    const actualChapter = Number(actual);
    return (
      Number.isFinite(expectedChapter) &&
      Number.isFinite(actualChapter) &&
      actualChapter >= expectedChapter
    );
  }
  return expected === "any" || expected === actual;
}

export class GuideQuestController {
  private readonly definitions: GuideQuestDefinition[];
  private readonly progress = new Map<string, GuideQuestProgressState>();

  constructor(
    definitions: readonly GuideQuestDefinition[],
    initialState: GuideQuestSaveState = { progress: {} },
  ) {
    this.definitions = definitions
      .filter((d) => d.enabled)
      .map((d) => ({ ...d, targetCount: Math.max(1, Math.floor(d.targetCount)) }))
      .sort((a, b) => a.order - b.order || a.questId.localeCompare(b.questId));

    for (const def of this.definitions) {
      this.progress.set(
        def.questId,
        normalizeProgress(def.questId, def.targetCount, initialState.progress[def.questId]),
      );
    }

    for (const [questId, state] of Object.entries(initialState.progress)) {
      if (this.progress.has(questId)) continue;
      this.progress.set(questId, {
        questId: state.questId || questId,
        progress: Math.max(0, Math.floor(state.progress ?? 0)),
        completed: Boolean(state.completed),
        claimed: Boolean(state.claimed),
      });
    }
  }

  active(): ActiveGuideQuest | null {
    const def = this.definitions.find((d) => !this.stateOf(d).claimed);
    if (!def) return null;
    const state = this.stateOf(def);
    return {
      definition: def,
      progress: state.progress,
      targetCount: def.targetCount,
      completed: state.completed,
      claimed: state.claimed,
    };
  }

  ingest(event: GuideQuestEvent): boolean {
    const active = this.active();
    if (!active) return false;
    const def = active.definition;
    if (def.objectiveKind !== event.kind) return false;
    if (!matchesTarget(def.objectiveTarget, event.target, event.kind)) return false;

    const prev = this.stateOf(def);
    if (prev.completed) return false;

    const nextProgress =
      typeof event.value === "number"
        ? Math.floor(event.value)
        : prev.progress + Math.max(1, Math.floor(event.amount ?? 1));
    const progress = Math.min(Math.max(0, nextProgress), def.targetCount);
    const next = {
      questId: def.questId,
      progress,
      completed: progress >= def.targetCount,
      claimed: false,
    };
    this.progress.set(def.questId, next);
    return next.progress !== prev.progress || next.completed !== prev.completed;
  }

  claimCurrent(): ClaimGuideQuestResult | null {
    const active = this.active();
    if (!active || !active.completed || active.claimed) return null;

    const def = active.definition;
    this.progress.set(def.questId, {
      questId: def.questId,
      progress: def.targetCount,
      completed: true,
      claimed: true,
    });
    return { questId: def.questId, rewardBundleId: def.rewardBundleId };
  }

  toState(): GuideQuestSaveState {
    return {
      progress: Object.fromEntries(
        [...this.progress.entries()]
          .filter(([, v]) => v.progress > 0 || v.completed || v.claimed)
          .map(([k, v]) => [k, { ...v }]),
      ),
    };
  }

  private stateOf(def: GuideQuestDefinition): GuideQuestProgressState {
    const state = this.progress.get(def.questId);
    if (state) return state;
    const next = normalizeProgress(def.questId, def.targetCount);
    this.progress.set(def.questId, next);
    return next;
  }
}
